import { FC, useEffect, useMemo, useState } from 'react';
import { Box, CircleButton, convertToNumber, RailzInventoryItem, RailzTaxRate, RailzTrackingCategory } from 'vopay';
import { buildTableFormRows, LineItem } from '../buildInvoicesForm.helper';
import { useForm } from '../../../Form/utils';
import { useClientData } from '@/providers/ClientData.provider';
import { useTranslation } from 'react-i18next';
interface InvoiceTableRowProps {
  index: number;
  key: string;
  inventory: RailzInventoryItem[];
  salesTaxes: RailzTaxRate[];
  trackingCategories: RailzTrackingCategory[];
  values: LineItem;
  nextRowUpdated: boolean;
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope';
  onChange: (index: number, LineItem: LineItem, isValid: boolean) => void;
  onDelete: (index: number) => void;
}

const InvoiceTableRow: FC<InvoiceTableRowProps> = ({
  index,
  key,
  inventory,
  salesTaxes,
  trackingCategories,
  values,
  nextRowUpdated,
  taxCalculation,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation('translations');
  const handleDelete = () => {
    onDelete(index);
  };
  const {
    response: { Country },
  } = useClientData();
  const currency = useMemo(() => {
    if (Country === 'US') {
      return 'USD';
    }
    return 'CAD';
  }, [Country]);
  const tableFormRows = buildTableFormRows(inventory, salesTaxes, trackingCategories, currency, taxCalculation, t);
  const [previousAmount, setPreviousAmount] = useState('');

  const { renderFormElements, ctx } = useForm<LineItem, LineItem>({
    config: tableFormRows,
    initialValues: values,
    onSubmit: () => {},
  });

  const formElements = renderFormElements({
    config: tableFormRows,
    override(fieldData) {
      return fieldData.template;
    },
  });
  useEffect(() => {
    if (nextRowUpdated) {
      tableFormRows.forEach((element) => {
        if (element.fieldName) {
          ctx.setFieldTouched(element.fieldName, true);
        }
      });
    }
  }, [nextRowUpdated]);

  useEffect(() => {
    if (taxCalculation === 'outOfScope') {
      ctx.setFieldValue('TaxRateID', '');
    }
  }, [taxCalculation]);

  useEffect(() => {
    onChange(index, ctx.values, ctx.isValid);
  }, [ctx.values, ctx.isValid]);

  useEffect(() => {
    if (ctx.values.Quantity && ctx.values.Rate) {
      const quantity = convertToNumber(ctx.values.Quantity) || 0;
      const rate = convertToNumber(ctx.values.Rate) || 0;
      const amount = quantity * rate;
      ctx.setFieldValue('Amount', amount.toString());
      setPreviousAmount(amount.toString());
    }
  }, [ctx.values.Quantity, ctx.values.Rate]);
  useEffect(() => {
    if (ctx.values.Amount !== previousAmount) {
      setPreviousAmount(ctx.values.Amount.toString());
      const quantity = convertToNumber(ctx.values.Quantity) || 1;
      const amount = convertToNumber(ctx.values.Amount) || 0;
      ctx.setFieldValue('Rate', (amount / quantity).toFixed(2));
    }
  }, [ctx.values.Amount]);
  return (
    <tr key={key}>
      <td>{index + 1}</td>
      <td>{formElements[0]}</td>
      <td>{formElements[1]}</td>
      <td style={{ maxWidth: '56px' }}>{formElements[2]}</td>
      <td style={{ maxWidth: '176px' }}>{formElements[3]}</td>
      <td style={{ maxWidth: '241px' }}>{formElements[4]}</td>
      {taxCalculation !== 'outOfScope' && <td>{formElements[5]}</td>}
      <td>
        <Box gapSize="8px" ai="center">
          {formElements[6]}
          <CircleButton icon="close" iconFill={true} colorScheme="destructive" size="small" onClick={handleDelete} />
        </Box>
      </td>
    </tr>
  );
};

export default InvoiceTableRow;
